import IvicosStrings from 'kits/language/stringKit';
import { useState } from 'react';
import Swal from 'sweetalert2';

const useBrowserNotificationData = (): {
    openNotificationSettings: () => void;
    permission: NotificationPermission;
    isNotificationSupported: boolean;
} => {
    const isNotificationSupported = typeof window !== 'undefined' && 'Notification' in window;
    const [permission, setPermission] = useState<NotificationPermission>(() => (isNotificationSupported ? Notification.permission : 'denied'));
    const chosenLanguage = localStorage.getItem('chosenLanguage') || 'de';

    const getBrowser = (): string => {
        const ua = navigator.userAgent.toLowerCase();
        const isBrave = (navigator as any).brave?.isBrave?.() || false;

        if (isBrave) return 'Brave';
        if (ua.includes('edg/')) return 'Edge';
        if (ua.includes('opr/') || ua.includes('opera')) return 'Opera';
        if (ua.includes('chrome') && !ua.includes('chromium')) return 'Chrome';
        if (ua.includes('firefox')) return 'Firefox';
        if (ua.includes('safari') && !ua.includes('chrome')) return 'Safari';

        return 'Other';
    };

    const getBrowserSpecificData = () => {
        const browser = getBrowser();
        const origin = encodeURIComponent(window.location.origin);
        const siteSettingsUrl = browser === 'Firefox' ? 'about:preferences#privacy' : `${browser.toLowerCase()}://settings/content/siteDetails?site=${origin}`;
        const guideUrl = chosenLanguage === 'en' ? 'https://ivicos.eu/notifications' : 'https://ivicos.eu/mitteilungen';

        const buttonStyle = 'padding: 5px 10px; background-color: #005A9E; color: white; border: none; cursor: pointer;';
        const inputStyle = 'flex: 1; padding: 5px; text-align: center;';
        const fluentClass = 'fluent-ui-font';

        return {
            browser,
            permissionRequestHtml: `
                <p id="requestClickMessage" class="${fluentClass}">${IvicosStrings.notificationRequestMessage}</p>
                <button id="requestPermissionButton" style="${buttonStyle}" class="${fluentClass}">${IvicosStrings.notificationRequestButton}</button>
            `,
            instructionHtml: `
                <div class="${fluentClass}">
                    <p>${IvicosStrings.notificationCopyInstruction}</p>
                    <div style="display: flex; gap: 10px; align-items: center;">
                        <input type="text" id="copyInput" value="${siteSettingsUrl}" readonly style="${inputStyle}" onclick="this.select()" />
                        <button id="copyButton" style="${buttonStyle}">${IvicosStrings.notificationCopyButton}</button>
                    </div>
                </div>
            `,
            footerHtml: `
                <p class="${fluentClass}">${IvicosStrings.notificationGuideText}</p>
                <a href="${guideUrl}" target="_blank" id="siteSettingsLink" style="color: #005A9E;" class="${fluentClass}">${IvicosStrings.notificationGuideLinkText}</a>
                <br><br>
                <button id="closeButton" style="margin-top: 10px; padding: 5px 15px; color: #000; border: none; cursor: pointer;" class="${fluentClass}">${IvicosStrings.notificationCloseButton}</button>
            `,
            icon: `<img src="${process.env.PUBLIC_URL}/favicons/favicon-96x96.png" alt="info icon" />`
        };
    };

    const bindEventListeners = () => {
        const permissionButton = document.getElementById('requestPermissionButton') as HTMLButtonElement;
        const clickMessage = document.getElementById('requestClickMessage') as HTMLParagraphElement;
        const copyButton = document.getElementById('copyButton') as HTMLButtonElement;
        const inputField = document.getElementById('copyInput') as HTMLInputElement;
        const closeButton = document.getElementById('closeButton');

        if (permissionButton && clickMessage && isNotificationSupported) {
            permissionButton.onclick = () => {
                Notification.requestPermission().then((newPermission) => {
                    setPermission(newPermission);
                    if (newPermission === 'granted') {
                        permissionButton.style.backgroundColor = '#fff';
                        permissionButton.style.color = '#000';
                        permissionButton.textContent = `✅ ${IvicosStrings.notificationEnabledText}`;
                        clickMessage.textContent = '';
                    }
                });
            };
        }

        if (copyButton && inputField) {
            copyButton.onclick = () => {
                inputField.select();
                navigator.clipboard.writeText(inputField.value).then(() => {
                    copyButton.style.backgroundColor = '#fff';
                    copyButton.style.color = '#000';
                    copyButton.textContent = `✅ ${IvicosStrings.notificationCopiedText}`;
                });
            };
        }

        if (closeButton) {
            closeButton.addEventListener('click', () => Swal.close());
        }
    };

    const openNotificationSettings = (): void => {
        if (!isNotificationSupported) return;
        const { permissionRequestHtml, instructionHtml, footerHtml, icon } = getBrowserSpecificData();
        Swal.fire({
            title: IvicosStrings.notificationTitle,
            html: permission === 'default' ? permissionRequestHtml : instructionHtml,
            footer: footerHtml,
            iconHtml: icon,
            width: '400px',
            heightAuto: false,
            showConfirmButton: false,
            showCancelButton: false,
            allowOutsideClick: false,
            allowEscapeKey: false,
            allowEnterKey: false,
            backdrop: true,
            customClass: {
                icon: 'no-border-icon',
                popup: 'fluent-ui-font'
            },
            didOpen: bindEventListeners
        });
    };

    return { openNotificationSettings, permission, isNotificationSupported };
};

export default useBrowserNotificationData;
